import { Link } from 'react-router-dom';
import {
  PhoneCall,
  Mic,
  Users,
  CreditCard,
  MessageSquare,
  GitBranch,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const features = [
  {
    icon: PhoneCall,
    title: 'IVR call routing',
    body: 'Build multi-level IVR trees with DTMF menus, conditional branches, and per-node auth — all configured in the portal, never hardcoded.',
  },
  {
    icon: Mic,
    title: 'Recording library',
    body: 'Upload audio or generate prompts from text. Every recording is versioned, folder-organized, and securely stored.',
  },
  {
    icon: Users,
    title: 'Conferencing',
    body: 'Live dial-in conference rooms with host controls, recording, and caller-PIN access modes built in.',
  },
  {
    icon: CreditCard,
    title: 'IVR billing',
    body: 'PCI-compliant DTMF payment collection over the phone, plus subscription and overage billing in the portal.',
  },
  {
    icon: MessageSquare,
    title: 'SMS',
    body: 'Outbound and inbound text messaging. Sender IDs and templates are configured per reseller — fully white-labeled.',
  },
  {
    icon: GitBranch,
    title: 'White-label portal',
    body: 'Every reseller gets their own branded domain, color scheme, and email templates. Your customers never see Ivrello.',
  },
] as const;

export function Home() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
        <div className="max-w-3xl space-y-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            White-label telephony platform
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            Cloud IVR,<br />reimagined.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Ivrello is a white-label telephony platform for resellers and their customers — IVR,
            recordings, conferencing, billing, and SMS, all in one portal.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button asChild size="lg">
              <Link to="/contact">
                Request access
                <ArrowRight className="ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/login">Log in</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Feature grid ─────────────────────────────────────────────────── */}
      <section className="border-t border-border bg-secondary/40">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="mb-10 sm:mb-12 max-w-xl">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              Everything your customers need — one platform
            </h2>
            <p className="mt-2 text-muted-foreground text-sm">
              A fully managed call platform — no scripting required. Every call flow lives in the
              portal and database.
            </p>
          </div>
          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-background p-5 sm:p-6 space-y-3 hover:border-primary/40 transition-colors"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="font-semibold text-sm tracking-tight">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Built for resellers ──────────────────────────────────────────── */}
      <section className="border-t border-border">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid gap-10 md:grid-cols-2 md:items-start">
            <div className="space-y-4 max-w-md">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Reseller-first architecture
              </p>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight leading-snug">
                Sell telephony services under your own brand
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every Ivrello account is isolated by tenant. Resellers configure their own
                carriers, billing plans, feature limits, and branding. Customers see only the
                reseller portal — never the platform beneath it.
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/contact">Talk to us about reseller access</Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                { label: 'Custom domain', detail: 'Route your own subdomain or apex domain to the portal.' },
                { label: 'Branded email', detail: 'All transactional email uses your sender name and templates.' },
                { label: 'Per-reseller limits', detail: 'Set storage, channel, and node limits independently per account.' },
                { label: 'RTL support', detail: 'Hebrew and Yiddish accounts automatically activate right-to-left layout.' },
              ].map(({ label, detail }) => (
                <div key={label} className="rounded-md border border-border p-4 space-y-1">
                  <p className="text-sm font-semibold">{label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA strip ────────────────────────────────────────────────────── */}
      <section className="border-t border-border bg-secondary/40">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="min-w-0">
            <p className="font-semibold tracking-tight">Ready to get started?</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Contact us to request reseller access or learn more about the platform.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/contact">Request access</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
